import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = { current: "", next: "", confirm: "" };

export default function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (f.next !== f.confirm) return toast.error("New passwords do not match");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      localStorage.setItem("bk_token", data.token);
      toast.success("Password changed");
      setOpen(false);
      setF(empty);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, testid) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input type="password" value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} data-testid={testid} autoComplete={key === "current" ? "current-password" : "new-password"} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setF(empty); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="nav-change-password-button">
          <KeyRound className="h-4 w-4" />
          <span className="hidden sm:inline">Password</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Change Admin Password</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {field("current", "Current password", "change-password-current-input")}
          {field("next", "New password", "change-password-new-input")}
          {field("confirm", "Confirm new password", "change-password-confirm-input")}
          <p className="text-xs text-muted-foreground">At least 8 characters, with letters and numbers. Other admin sessions will be signed out.</p>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving || !f.current || !f.next} data-testid="change-password-submit-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
